import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../../common/breadcrumb";
import { Card, CardBody, Container, Row } from "reactstrap";
import { Button, Grid, TextField } from "@mui/material";
import { PersonSearch, Refresh, SimCardDownloadRounded } from "@mui/icons-material";
import { ToastContainer, toast } from "react-toastify";
import moment from "moment";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import CommonLoader from "../../common/commonLoader";
import CommonTableAp from "../../common/commonTableAp";
import CommonAccordion from "../../common/commonAccordion";
import {
  vendorAmendmentListTableData,
  vendorAmendmentListTableHeader,
} from "../../common/commonConstantAp";
import {
  getAllVendorAmendmentList,
  searchVendorAmendmentListData,
  exportToExcelVendorAmendmentList,
} from "../../api/ap_services";

const useStyles = makeStyles((theme) => ({
  btnClass: {
    padding: "0.5rem 1.5rem!important",
    fontWeight: 600,
  },
  btnJust: {
    display: "flex!important",
    justifyContent: "end!important",
  },
  cardbody: {
    padding: `${theme.spacing(1.8, 1.3)} !important`,
    [theme.breakpoints.down("sm")]: {
      padding: `${theme.spacing(0)} !important`,
      margin: `${theme.spacing(0, 1)} !important`,
    },
  },
  muiGrid: {
    marginBottom: theme.spacing(1),
  },
}));

const filterInitialValues = {
  searchName: "",
  searchVendorCode: "",
  searchAmendmentNo: "",
};

const isApVendorUser = () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
    return Number(userData.groupId) === 0 && Number(userData.locationId) === 2;
  } catch {
    return false;
  }
};

const VendorAmendmentList = () => {
  const history = useNavigate();
  const classes = useStyles();
  const [totalRecord, setTotalRecord] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchData, setSearchData] = useState(filterInitialValues);
  const [isSearchActive, setIsSearchActive] = useState(false);

  useEffect(() => {
    if (isApVendorUser()) {
      history(`${process.env.PUBLIC_URL}/dashboard`, { replace: true });
    }
  }, [history]);

  const loadListData = (pageNo = page, size = rowsPerPage) => {
    setLoading(true);
    getAllVendorAmendmentList(pageNo, size)
      .then((res) => {
        const raw = res?.response?.responseObject ?? [];
        setData(Array.isArray(raw) ? raw : []);
        setTotalRecord(res?.response?.responseMap?.totalCount ?? 0);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };

  const loadSearchData = (pageNo = page, size = rowsPerPage) => {
    setLoading(true);
    searchVendorAmendmentListData(searchData, pageNo, size)
      .then((res) => {
        const raw = res?.response?.responseObject ?? [];
        setData(Array.isArray(raw) ? raw : []);
        setTotalRecord(res?.response?.responseMap?.totalCount ?? 0);
      })
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  };

  const fetchPage = (pageNo, size) => {
    if (isSearchActive) {
      loadSearchData(pageNo, size);
    } else {
      loadListData(pageNo, size);
    }
  };

  useEffect(() => {
    if (!isApVendorUser()) {
      loadListData(0, rowsPerPage);
    }
  }, []);

  const onFilterInputChange = (event) => {
    const { name, value } = event.target;
    setSearchData((prev) => ({ ...prev, [name]: value }));
  };

  const filterData = () => {
    setIsSearchActive(true);
    setPage(0);
    loadSearchData(0, rowsPerPage);
  };

  const resetSearch = () => {
    setSearchData(filterInitialValues);
    setIsSearchActive(false);
    setPage(0);
    loadListData(0, rowsPerPage);
  };

  const handleExportVendorAmendmentList = () => {
    setLoading(true);
    const dateTime = "Vendor_Amendment_List".concat(
      "_",
      moment(new Date()).format("DD_MM_YYYY_HH_mm_SS"),
    );
    const exportFilters = isSearchActive ? searchData : filterInitialValues;
    exportToExcelVendorAmendmentList(exportFilters, dateTime)
      .then(() => {
        toast.success("Excel downloaded successfully");
      })
      .catch(() => {
        toast.error("Failed to download Excel");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const redirectToAmendmentForm = (row) => {
    const id =
      row?.vendorAmendmentListId ??
      row?.vendorAmendmentId ??
      row?.vendor_amendment_id;
    if (id == null) return;
    history(`${process.env.PUBLIC_URL}/master/vendorAmendmentForm/${id}`);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchPage(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const size = event.target.value;
    setRowsPerPage(size);
    setPage(0);
    fetchPage(0, size);
  };

  if (isApVendorUser()) {
    return null;
  }

  return (
    <Fragment>
      {loading ? <CommonLoader /> : null}
      <Breadcrumb title="Vendor Amendment" />
      <Container fluid>
        <Row>
          <CommonAccordion accordionTitle={"Advanced Search"}>
            <Grid container columnSpacing={2} className={classes.muiGrid}>
              <Grid item sm={3} xs={12}>
                <TextField
                  fullWidth
                  label="Company Name"
                  size="small"
                  name="searchName"
                  value={searchData.searchName}
                  onChange={onFilterInputChange}
                  placeholder="Enter company name"
                />
              </Grid>
              <Grid item sm={3} xs={12}>
                <TextField
                  fullWidth
                  label="Vendor Code"
                  size="small"
                  name="searchVendorCode"
                  value={searchData.searchVendorCode}
                  onChange={onFilterInputChange}
                  placeholder="Enter vendor code"
                />
              </Grid>
              <Grid item sm={3} xs={12}>
                <TextField
                  fullWidth
                  label="Amendment No"
                  size="small"
                  name="searchAmendmentNo"
                  value={searchData.searchAmendmentNo}
                  onChange={onFilterInputChange}
                  placeholder="e.g. VA202600001"
                />
              </Grid>
              <Grid
                item
                sm={3}
                xs={12}
                sx={{
                  margin: "auto",
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <Button
                  color="primary"
                  variant="contained"
                  type="button"
                  className={classes.btnClass}
                  onClick={filterData}
                >
                  <PersonSearch />
                </Button>
                <Button
                  color="primary"
                  sx={{ marginLeft: 1 }}
                  variant="contained"
                  type="button"
                  className={classes.btnClass}
                  onClick={resetSearch}
                >
                  <Refresh />
                </Button>
                <Button
                  color="primary"
                  sx={{ marginLeft: 1 }}
                  variant="contained"
                  type="button"
                  className={classes.btnClass}
                  onClick={handleExportVendorAmendmentList}
                  title="Download Excel"
                >
                  <SimCardDownloadRounded />
                </Button>
              </Grid>
            </Grid>
          </CommonAccordion>
          <Card>
            <CardBody className={classes.cardbody}>
              <CommonTableAp
                tableData={data}
                tableHeaders={vendorAmendmentListTableHeader}
                tableColumns={vendorAmendmentListTableData}
                editId="vendorAmendmentListId"
                editMode
                onOpenModal={redirectToAmendmentForm}
                rowsPerPageOptions={[25, 50, 100]}
                count={totalRecord}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ float: "right" }}
              />
            </CardBody>
          </Card>
        </Row>
      </Container>
      <ToastContainer autoClose={1000} />
    </Fragment>
  );
};

export default VendorAmendmentList;
